import { apiErrorMessage } from '@/api/client';
import { EnumEditor } from '@/components/admin/EnumEditor';
import { RegexTester, SAMPLE_TEXT } from '@/components/admin/RegexTester';
import { GHOST_BUTTON, LARGE_MODAL, typeBadgeStyle } from '@/components/admin/ui';
import { Modal } from '@/components/common/Modal';
import { PadlockIcon } from '@/components/common/icons';
import type { ItemModel, UpdateItem } from '@/types/categories.types';
import type { FC } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ItemEditorModalProps {
  item: ItemModel;
  onSave: (body: UpdateItem) => Promise<void>;
  onClose: () => void;
}

/**
 * The prototype's configuration editor for one item: the prompt, the regex (with the tester) or
 * the ENUM values. An item the caller may not change (`editable` from the API) opens read-only
 * under the lock banner, with "Zavřít" and no "Uložit".
 */
export const ItemEditorModal: FC<ItemEditorModalProps> = ({ item, onSave, onClose }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const readOnly = !item.editable;
  const [draft, setDraft] = useState(item.config);
  const [values, setValues] = useState(item.values);
  const [sample, setSample] = useState(SAMPLE_TEXT);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEnum = item.type === 'ENUM';
  const isRegex = item.type === 'regex';

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(isEnum ? { values } : { config: draft });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t('admin.saveFailed')));
      setIsSaving(false);
    }
  };

  const lockText =
    item.author === 'default' ? t('admin.editor.lockSystem') : t('admin.editor.lockOther', { name: item.author });

  return (
    <Modal onClose={onClose} maxWidth={920} labelledBy={titleId} className={LARGE_MODAL}>
      <div className="flex items-start gap-3 border-b border-line px-[22px] pb-3.5 pt-[18px]">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <div className="text-[10.5px] font-semibold uppercase tracking-[.1em] text-label-2">
            {t(`admin.editor.kind.${item.type}`)}
          </div>
          <div id={titleId} className="text-base font-bold text-primary">
            {item.name}
          </div>
          <div className="font-mono text-[11px] text-label-2">{item.id}</div>
        </div>
        <span className="flex-none px-2 py-[3px] text-[11px] font-semibold" style={typeBadgeStyle(item.type)}>
          {t(`admin.types.${item.type}`)}
        </span>
      </div>

      {readOnly ? (
        <div className="flex items-center gap-[9px] border-b border-[#f0e0bd] bg-[#fbf1de] px-[22px] py-[9px] text-xs text-[#5c3b00]">
          <PadlockIcon size={13} className="flex-none" />
          <span>{lockText}</span>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto px-[22px] py-4">
        {isEnum ? (
          readOnly ? (
            <div className="flex min-h-0 flex-1 flex-col gap-1.5">
              <span className="text-[11.5px] text-muted">
                {t('admin.editor.values', { count: item.values.length, n: item.values.length.toLocaleString('cs-CZ') })}
              </span>
              <div className="min-h-[140px] flex-1 overflow-auto border border-line bg-wash">
                {item.values.map((value, index) => (
                  <div
                    key={`${index}-${value}`}
                    className="flex items-center gap-2 border-b border-hairline py-[5px] pl-0 pr-2.5 text-[12.5px] text-ink"
                  >
                    <span className="w-11 flex-none text-right text-[10.5px] text-faint">{index + 1}</span>
                    {value}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EnumEditor values={values} onChange={setValues} />
          )
        ) : (
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            readOnly={readOnly}
            aria-label={t(`admin.editor.kind.${item.type}`)}
            placeholder={t(`admin.editor.placeholder.${item.type}`)}
            className={`w-full resize-y rounded border border-control bg-wash px-3.5 py-3 leading-[1.6] text-ink focus:border-primary focus:outline-none ${
              isRegex
                ? 'min-h-[90px] flex-none font-mono text-[12.5px]'
                : 'min-h-[260px] flex-1 font-[inherit] text-[13.5px]'
            }`}
          />
        )}
        <div className="text-[11.5px] leading-[1.5] text-label-2">{t(`admin.editor.hint.${item.type}`)}</div>
        {isRegex ? (
          <div className="mt-2 border-t border-hairline pt-3.5">
            <RegexTester pattern={draft} sample={sample} onSampleChange={setSample} />
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 text-xs text-danger">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t border-hairline px-[22px] pb-[18px] pt-3">
        <button type="button" onClick={onClose} className={GHOST_BUTTON}>
          {readOnly ? t('admin.editor.close') : t('admin.editor.cancel')}
        </button>
        {readOnly ? null : (
          <button
            type="button"
            onClick={save}
            disabled={isSaving}
            className="cursor-pointer rounded border-none bg-primary px-4 py-[9px] text-[12.5px] font-semibold text-white hover:bg-primary-deep disabled:cursor-progress disabled:opacity-70"
          >
            {t('admin.editor.save')}
          </button>
        )}
      </div>
    </Modal>
  );
};
